/**
 * TradeRecordDetail — builds the full detail view for a single quote or order.
 *
 * Assembles header, line-item content, and footer sections from <template>
 * elements, then hands them to renderTradeDetailScaffold for DOM insertion.
 * Type-specific behavior is injected via the config object (populateItem,
 * populateFooter, buildEmptyState).
 *
 * Usage:
 *   const ctx = window.buildTradeRecordDetail(record, {
 *     type: 'quote',
 *     containerEl: document.getElementById('detail'),
 *     headerTemplateId: 'tpl-quote-header',
 *     itemTemplateId: 'tpl-quote-item',
 *     footerTemplateId: 'tpl-quote-footer',
 *     optionTemplateId: 'tpl-item-option',
 *     populateItem: function (clone, item, ctx) { … },
 *     populateFooter: function (clone, ctx) { … },
 *   });
 */
(function () {
	window.buildTradeRecordDetail = function (record, config) {
		const formatCurrency = window.formatTradeAmount;
		const itemTitleField = config.itemTitleField || 'title';
		const dateField = config.dateField || 'createdAt';

		const formattedDate = record[dateField]
			? new Date(record[dateField]).toLocaleDateString('en-US', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
				})
			: 'N/A';

		const totalMSRP = (record.items || []).reduce(function (sum, item) {
			return sum + item.msrp * item.quantity;
		}, 0);

		const hasItems = record.items && record.items.length > 0;
		const showDiscount = config.showDiscount || false;

		const getSaleDiscountPercentage = function (item) {
			const msrp = Number(item.msrp);
			const discountPrice = Number(item.discountPrice);
			if (!msrp || !discountPrice || discountPrice <= 0) return null;
			return Math.floor((discountPrice / msrp) * 100);
		};

		const ctx = {
			formattedDate: formattedDate,
			totalMSRP: totalMSRP,
			hasItems: hasItems,
			formatCurrency: formatCurrency,
			showDiscount: showDiscount,
		};

		const buildHeader = function () {
			const clone = window.cloneTemplate(config.headerTemplateId);
			clone.querySelector('[data-tpl-id]').textContent = '#' + record.displayId;
			clone.querySelector('[data-tpl-title]').textContent = record.title;
			clone.querySelector('[data-tpl-shipping-method]').textContent = record.shippingMethodLabel || record.shippingMethod;
			clone.querySelector('[data-tpl-address]').textContent =
				(record.shippingAddress.address1 || '') +
				' ' +
				(record.shippingAddress.address2 || '') +
				' ' +
				(record.shippingAddress.city || '') +
				' ' +
				(record.shippingAddress.state || '') +
				' ' +
				(record.shippingAddress.zipPostalCode || '') +
				' ' +
				(record.shippingAddress.country || '');
			clone.querySelector('[data-tpl-date]').textContent = formattedDate;
			return clone;
		};

		const buildContent = function () {
			const fragment = document.createDocumentFragment();

			if (!hasItems) {
				if (typeof config.buildEmptyState === 'function') {
					fragment.appendChild(config.buildEmptyState());
				}
				return fragment;
			}

			const itemsContainer = document.createElement('div');
			itemsContainer.className = 'quote-items';

			record.items.forEach(function (item) {
				if (!item) return;

				const priceFormatted = formatCurrency(item.price);
				const msrpFormatted = formatCurrency(item.msrp);
				const discountPriceFormatted = formatCurrency(item.discountPrice);

				const clone = window.cloneTemplate(config.itemTemplateId);
				const root = clone.querySelector('.quote-item');
				root.setAttribute('data-sku', item.sku);

				// Image
				if (item.image && item.image.url) {
					const img = clone.querySelector('[data-tpl-image]');
					img.src = item.image.url;
					img.alt = item.image.altText || '';
					img.style.display = '';
					clone.querySelector('[data-tpl-image-placeholder]').remove();
				} else {
					clone.querySelector('[data-tpl-image]').remove();
				}

				// Title, SKU
				clone.querySelector('[data-tpl-title]').textContent = item[itemTitleField];
				clone.querySelector('[data-tpl-sku]').textContent = 'SKU: ' + item.sku;

				// Dimensions
				if (item.dimensions.width) {
					const w = clone.querySelector('[data-tpl-width]');
					w.textContent = 'WIDTH(IN): ' + item.dimensions.width;
					w.style.display = '';
				}
				if (item.dimensions.height) {
					const h = clone.querySelector('[data-tpl-height]');
					h.textContent = 'HEIGHT(IN): ' + item.dimensions.height;
					h.style.display = '';
				}
				if (item.dimensions.depth) {
					const d = clone.querySelector('[data-tpl-depth]');
					d.textContent = 'DEPTH(IN): ' + item.dimensions.depth;
					d.style.display = '';
				}

				// Options
				const optionsSlot = clone.querySelector('[data-tpl-options]');
				item.options.forEach(function (option) {
					const optClone = window.cloneTemplate(config.optionTemplateId);
					if (option.swatchImageUrl) {
						const swatch = optClone.querySelector('[data-tpl-swatch]');
						swatch.src = option.swatchImageUrl;
						swatch.alt = option.name;
						swatch.style.display = '';
					} else {
						optClone.querySelector('[data-tpl-swatch]').remove();
					}
					optClone.querySelector('[data-tpl-option-text]').textContent =
						option.type + ': ' + option.name;
					optionsSlot.appendChild(optClone);
				});

				// Prices
				clone.querySelector('[data-tpl-mobile-price]').textContent =
					'TRADE PRICE: ' + priceFormatted;
				clone.querySelector('[data-tpl-msrp]').textContent = 'MSRP: ' + msrpFormatted;

				const displayDiscountPercentage =
					item.tradeDiscountPercentage ?? record.tradeDiscountPercentage;
				if (showDiscount && item.onSale && item.discountPrice > 0) {
					const discountEl = clone.querySelector('[data-tpl-discount]');
					const saleDiscountPercentage = getSaleDiscountPercentage(item);
					discountEl.textContent =
						'DISCOUNT' +
						(saleDiscountPercentage ? ' (' + saleDiscountPercentage + '% OFF)' : '') +
						': ' +
						discountPriceFormatted;
					discountEl.style.display = '';
				} else if (showDiscount && displayDiscountPercentage) {
					const discountEl = clone.querySelector('[data-tpl-discount]');
					discountEl.textContent =
						'TRADE DISCOUNT (' +
						displayDiscountPercentage +
						'%): ' +
						discountPriceFormatted;
					discountEl.style.display = '';
				}

				clone.querySelector('[data-tpl-mobile-trade-price]').textContent =
					'TRADE PRICE: ' + priceFormatted;
				clone.querySelector('[data-tpl-subtotal]').textContent = formatCurrency(
					item.price * item.quantity,
				);

				// Type-specific item population
				if (typeof config.populateItem === 'function') {
					config.populateItem(clone, item, ctx);
				}

				itemsContainer.appendChild(clone);
			});

			fragment.appendChild(itemsContainer);
			fragment.appendChild(
				window.buildTradeSummaryDesktop(
					record,
					totalMSRP,
					showDiscount && record.tradeDiscountPercentage,
					formatCurrency,
					config.type,
				),
			);

			return fragment;
		};

		const buildFooter = function () {
			const clone = window.cloneTemplate(config.footerTemplateId);

			// Insert summary block
			const summarySlot = clone.querySelector('[data-tpl-summary-slot]');
			summarySlot.replaceWith(
				window.buildTradeSummaryBlock(
					record,
					config.type,
					formattedDate,
					totalMSRP,
					showDiscount && record.tradeDiscountPercentage,
					formatCurrency,
				),
			);

			// Type-specific footer population
			if (typeof config.populateFooter === 'function') {
				config.populateFooter(clone, ctx);
			}

			return clone;
		};

		window.renderTradeDetailScaffold({
			containerEl: config.containerEl,
			buildHeader: buildHeader,
			buildContent: buildContent,
			buildFooter: buildFooter,
			hasContent: function () {
				return hasItems;
			},
		});

		return ctx;
	};
})();
